import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { io } from "socket.io-client";
import { useAuth } from "@workos-inc/authkit-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import { useCacheMutations, entryGroupsQueryKey } from "@/shared/cache";
import { timerSpansQueryKey } from "@/shared/config/queryKeys";
import { fetchLegacyEntriesFromEntryGroups } from "@/shared/utils/entryGroups";

export type Entry = {
  id: number;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  units_seconds?: number;
  start_time?: string;
  end_time?: string;
  application?: string;
  /** Matter-level billable flag. null = unknown, treated as non-billable for aggregation. */
  billable?: boolean | null;
  // Admin feedback fields
  approved?: boolean;
  needs_improvement?: boolean;
  attorney_feedback?: string | null;
};

type EntriesContextType = {
  // Data
  entries: Entry[];
  exportedEntries: Entry[];
  deletedEntries: Entry[];

  // Loading states
  isLoading: boolean;
  isProcessingEntry: boolean;
  processingApplication: string | null;

  // Refresh functions
  refreshRecords: () => Promise<void>;

  // Optimistic updates
  updateEntries: (updater: (prev: Entry[]) => Entry[]) => () => void;
  mutatingIds: Set<number>;
  setMutatingIds: React.Dispatch<React.SetStateAction<Set<number>>>;
};

const EntriesContext = createContext<EntriesContextType | undefined>(undefined);

export function EntriesProvider({ children }: { children: ReactNode }) {
  const { makeApiRequest } = useApi();
  const { user, isLoading: isAuthLoading } = useAuth();
  const location = useLocation();
  const shouldLoadLegacyRecords = !location.pathname.startsWith("/entries");
  const { refreshAll } = useCacheMutations();
  const queryClient = useQueryClient();
  const refreshAllRef = useRef(refreshAll);
  useEffect(() => { refreshAllRef.current = refreshAll; }, [refreshAll]);

  // Refetch the v2 smart-timer and its entry groups. Driven by the "timer_changed"
  // socket push (and, for a server-side force-close, the "entry processing
  // completed" event) so timer state converges across tabs/devices without polling.
  const invalidateTimerQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: timerSpansQueryKey });
    void queryClient.invalidateQueries({ queryKey: entryGroupsQueryKey });
  }, [queryClient]);
  const invalidateTimerQueriesRef = useRef(invalidateTimerQueries);
  useEffect(() => { invalidateTimerQueriesRef.current = invalidateTimerQueries; }, [invalidateTimerQueries]);
  const shouldLoadLegacyRecordsRef = useRef(shouldLoadLegacyRecords);
  useEffect(() => { shouldLoadLegacyRecordsRef.current = shouldLoadLegacyRecords; }, [shouldLoadLegacyRecords]);

  const [entries, setEntries] = useState<Entry[]>([]);
  const [exportedEntries, setExportedEntries] = useState<Entry[]>([]);
  const [deletedEntries, setDeletedEntries] = useState<Entry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingEntry, setIsProcessingEntry] = useState(false);
  const [processingApplication, setProcessingApplication] = useState<string | null>(null);
  const [mutatingIds, setMutatingIds] = useState<Set<number>>(new Set());
  const showedOfflineToast = useRef(false);

  // Ref so WebSocket handlers always read the current user (effect runs once with [])
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const updateEntries = useCallback((updater: (prev: Entry[]) => Entry[]) => {
    let snapshot: Entry[];
    setEntries(prev => { snapshot = prev; return updater(prev); });
    return () => setEntries(snapshot!);
  }, []);

  const refreshRecords = useCallback(async () => {
    try {
      const startTime = performance.now();

      const applyRecords = ({
        entries: transformedRecords,
        exportedEntries: transformedExported,
        deletedEntries: transformedDeleted,
      }: Awaited<ReturnType<typeof fetchLegacyEntriesFromEntryGroups>>) => {
        setEntries(transformedRecords);
        setExportedEntries(transformedExported);
        setDeletedEntries(transformedDeleted);
        return {
          recordCount: transformedRecords.length,
          exportedCount: transformedExported.length,
          deletedCount: transformedDeleted.length,
        };
      };

      const counts = applyRecords(
        await fetchLegacyEntriesFromEntryGroups({ makeApiRequest }),
      );

      logger.debug("Records loaded", {
        ...counts,
        durationMs: Math.round(performance.now() - startTime),
      });
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'refreshRecords' });
      }
      // Don't double-toast while the offline toast is already up.
      if (!showedOfflineToast.current) {
        toast.error("Failed to load entries", {
          id: "refresh-records-error",
          description: e instanceof Error ? e.message : undefined,
          duration: TOAST_DURATION.error,
        });
      }
    }
  }, [makeApiRequest]);

  // Initial data load - wait for authentication to complete
  useEffect(() => {
    if (!isAuthLoading && user) {
      if (!shouldLoadLegacyRecords) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      refreshRecords().finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, user, shouldLoadLegacyRecords]);

  // WebSocket connection for real-time entry processing updates
  useEffect(() => {
    // set up websocket connection
    const subdomain = window.location.hostname.split(".")[0];
    let API_URL_BASE = `https://${subdomain}api.hourglass.law`;

    if (window.location.hostname.includes("bpmlaw")) {
      API_URL_BASE = `https://hourglassapi.bpmlaw.com`;
    } else if (window.location.hostname.includes("tysonmendes")) {
      API_URL_BASE = `https://hourglassapi.tysonmendes.com`;
    } else if (window.location.hostname.includes("pregodonnell")) {
      API_URL_BASE = `https://hourglassapi.pregodonnell.com`;
    } else if (window.location.hostname.includes("localhost")) {
      API_URL_BASE = `http://localhost:8000`;
    } else if (window.location.hostname.includes("app.hourglass.law")) {
      API_URL_BASE = `https://api.hourglass.law`;
    }
    const socket = io(API_URL_BASE, {
      transports: ["websocket"], // server runs PM2 cluster without sticky sessions; polling is disabled server-side
      reconnectionAttempts: 30,
      reconnectionDelayMax: 15000,
    });

    const refreshEntriesData = async () => {
      try {
        if (shouldLoadLegacyRecordsRef.current) await refreshRecords();
        else await refreshAllRef.current();
      } catch (error) {
        if (error instanceof Error) {
          logger.captureException(error, { action: 'loadRecordsWebSocket' });
        }
      }
    };

    socket.on("connect", () => {
      logger.debug("WebSocket connected");
      // Gate on the offline toast so sub-second blips don't trigger a refetch.
      if (showedOfflineToast.current) {
        showedOfflineToast.current = false;
        toast.dismiss("ws-offline");
        toast.success("Reconnected", { id: "ws-reconnected", duration: 2000 });
        refreshEntriesData();
        // Socket.IO doesn't replay events missed while disconnected, so re-sync
        // the timer after any reconnect in case a start/stop/force-close was lost.
        invalidateTimerQueriesRef.current();
      }
    });

    socket.on("entry processing started", (data) => {
      if (data.email === userRef.current?.email) {
        setIsProcessingEntry(true);
        setProcessingApplication(data.application || null);
      }
    });

    socket.on("entry processing completed", (data) => {
      if (data.email === userRef.current?.email) {
        setIsProcessingEntry(false);
        setProcessingApplication(null);
        refreshEntriesData();
        // A running timer can be force-closed server-side while ingesting this
        // entry (target group turned immutable); re-sync so the UI clears it.
        invalidateTimerQueriesRef.current();
      }
    });

    socket.on("timer_changed", (data) => {
      if (data.email === userRef.current?.email) {
        invalidateTimerQueriesRef.current();
      }
    });

    // Listen for admin feedback updates on attorney entries
    socket.on("entry_feedback_updated", (data) => {
      if (data.email === userRef.current?.email) {
        refreshEntriesData();
      }
    });

    socket.on("connect_error", (error) => {
      logger.captureException(error, { action: 'websocketConnect' });
      if (!showedOfflineToast.current) {
        showedOfflineToast.current = true;
        toast.loading("Reconnecting to Hourglass…", {
          id: "ws-offline",
          duration: Infinity,
          description: "Your saved entries are safe. We'll refresh as soon as we're back online.",
        });
      }
    });

    socket.io.on("reconnect_failed", () => {
      showedOfflineToast.current = false;
      toast.dismiss("ws-offline");
      toast.error("Connection error", {
        id: "ws-failed",
        duration: Infinity,
        description: "We can't reach the server right now.",
        action: {
          label: "Refresh and try again",
          onClick: () => window.location.reload(),
        },
      });
    });

    return () => {
      socket.off("connect");
      socket.off("entry processing started");
      socket.off("entry processing completed");
      socket.off("timer_changed");
      socket.off("entry_feedback_updated");
      socket.off("connect_error");
      socket.io.off("reconnect_failed");
      socket.disconnect();
    };
  }, []);

  const value: EntriesContextType = {
    entries,
    exportedEntries,
    deletedEntries,
    isLoading,
    isProcessingEntry,
    processingApplication,
    refreshRecords,
    updateEntries,
    mutatingIds,
    setMutatingIds,
  };

  return (
    <EntriesContext.Provider value={value}>
      {children}
    </EntriesContext.Provider>
  );
}

export function useEntries() {
  const context = useContext(EntriesContext);
  if (context === undefined) {
    throw new Error("useEntries must be used within an EntriesProvider");
  }
  return context;
}
